import * as React from "react";
import { styled } from "styled-components";
import { Image } from "@ludens-reklame/rubics-v3-sdk/file/types";
import { transformImageUrl } from "@ludens-reklame/rubics-v3-sdk/file/utils";
import { mobileBreakPointCSS } from "../../defaults/theme.js";
import {
  Constrainer,
  HorizontalSpacer,
  VerticalSpacer,
} from "../../internal-components/Layout.js";
import { RichText } from "../../internal-components/RichText.js";

interface ImageObj {
  image?: Image;
  text?: string;
}

interface Props {
  title?: string;
  images?: ImageObj[];
}

export const ImageShowcase: React.FC<Props> = ({ title, images = [] }) => {
  const [showcasedIndex, setShowcasedIndex] = React.useState<number>(0);

  return (
    <VerticalSpacer>
      <HorizontalSpacer>
        <Constrainer $width="medium">
          {title && <Title>{title}</Title>}
          <FlexContainer>
            {images.map((item, index) => {
              if (!item.image || index > 3) return null;

              const isShowcased = index === showcasedIndex;

              return (
                <ImageWrapper
                  key={index}
                  role="button"
                  isShowcased={isShowcased}
                  onClick={() => {
                    setShowcasedIndex(index);
                  }}
                >
                  <StyledImage
                    backgroundImage={transformImageUrl(item.image, {
                      height: 1080,
                      width: 1920,
                    })}
                  />
                  <ImageContent isShowcased={isShowcased}>
                    {item.text && (
                      <TextWrapper>
                        <RichText
                          dangerouslySetInnerHTML={{ __html: item.text }}
                        />
                      </TextWrapper>
                    )}
                  </ImageContent>
                </ImageWrapper>
              );
            })}
          </FlexContainer>
        </Constrainer>
      </HorizontalSpacer>
    </VerticalSpacer>
  );
};

const FlexContainer = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing.s};
  flex-direction: column;

  @media all and (min-width: ${mobileBreakPointCSS}) {
    gap: ${(props) => props.theme.spacing.m};
    flex-direction: row;
  }
`;

const ImageWrapper = styled.div<{ isShowcased: boolean }>`
  position: relative;
  width: 100%;
  height: 250px;
  overflow: hidden;
  border-radius: ${(props) => props.theme.borderRadius};

  &::after {
    border-radius: ${(props) => props.theme.borderRadius};
  }

  @media all and (min-width: ${mobileBreakPointCSS}) {
    height: 564px;
    flex-grow: ${(props) => (props.isShowcased ? 4 : 1)};
    flex-shrink: 1;
    flex-basis: 0;
    transition: flex-grow 0.15s ease-in-out;
    cursor: pointer;
  }
`;

const Title = styled.h2`
  margin-bottom: ${(props) => props.theme.spacing.l};
`;

const ImageContent = styled.div<{ isShowcased: boolean }>`
  position: absolute;
  z-index: 1;
  bottom: 0;
  left: 0;
  right: 0;

  transition: opacity 0.2s ease-in-out;
  margin: ${(props) => props.theme.spacing.s};

  @media all and (min-width: ${mobileBreakPointCSS}) {
    margin: ${(props) => props.theme.spacing.l};
    opacity: ${(props) => (props.isShowcased ? 1 : 0)};
  }
`;

const StyledImage = styled.div<{ backgroundImage: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${(props) => props.backgroundImage});
  background-size: cover;
  background-position: center;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.1) 40%,
      rgba(0, 0, 0, 1) 100%
    );
    pointer-events: none;

    @media all and (min-width: ${mobileBreakPointCSS}) {
      background: linear-gradient(
        to bottom,
        rgba(0, 0, 0, 0.1) 50%,
        rgba(0, 0, 0, 1) 100%
      );
    }
  }
`;

const TextWrapper = styled.div`
  color: #fff;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-bottom: ${(props) => props.theme.spacing.xs} !important;
  }
`;
